/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import PropTypes from 'prop-types';
import { useMemo } from 'react';
import isEmpty from 'lodash/isEmpty';
import debounce from 'lodash/debounce';
import moment from 'moment';

import { Stack, Alert, InlineBulletList, Text, Badge, useFlags } from 'sarsaparilla';

import SearchResultCard from 'shared-ui/components/SearchResultCard/SearchResultCard';
import { showNoTicketRequired } from 'ui-timed-entry/src/utils/launchDarklyUtils';

export function TourList({
    tours,
    tourImageMap,
    tourRatingsMap,
    ticketTimesById,
    areTimesLoading,
    hasTimesLoadingError,
    filterDate,
    firstComeFirstServedTours,
    onCardOverEvent,
    farthestByTour,
}) {
    const releaseDateAndTimeFormat = 'll [at] LT z';

    const onCardOver = debounce((tourId) => {
        onCardOverEvent(tourId);
    }, 500);

    const { facilitiesUsingNoTicketRequired } = useFlags();
    const hasFilterDate = useMemo(() => {
        return Boolean(filterDate);
    }, [filterDate]);

    const getToursCardData = () => {
        return tours?.map((item) => {
            const {
                tour_id: tourId,
                facility_id: facilityId,
                tour_name: tourName,
            } = item;

            const tourKey = `${facilityId}_${tourId}`;
            let contentAriaLabel = tourName;

            const imageUrl =
                tourImageMap?.[tourKey]?.previewImage?.url ||
                tourImageMap?.[tourKey]?.heroImage?.url ||
                tourImageMap?.[tourKey]?.galleryImages?.[0]?.url;

            const rating = tourRatingsMap?.[tourId]?.averageRating;
            const reviewCount = tourRatingsMap?.[tourId]?.reviewCount;

            const availableTourTimes = [];
            const tourTimes = [];
            const walkupTourTimes = [];
            const availableTourTimesText = [];
            const tourTimesText = [];
            const walkupTourTimesText = [];
            const unreleasedDates = [];
            const unreleasedDatesText = [];

            let hasScheduledInventory = false;
            if (ticketTimesById?.[tourId]) {
                let alreadyShowedReleaseDate = false;
                ticketTimesById[tourId].forEach((timeItem) => {
                    hasScheduledInventory = timeItem.hasScheduledInventory;
                    if (timeItem.isAvailable) {
                        availableTourTimes.push(
                            <li key={timeItem.displayTime}>{timeItem.displayTime}</li>
                        );
                        tourTimes.push(
                            <li key={timeItem.displayTime}>{timeItem.displayTime}</li>
                        );
                        availableTourTimesText?.push(timeItem?.displayTime);
                        tourTimesText?.push(timeItem?.displayTime);
                    } else if (timeItem.unreleasedCount && !alreadyShowedReleaseDate) {
                        if (timeItem.primaryOpenMoment && !timeItem.primaryEmpty) {
                            const releaseDateAndTime = timeItem.primaryOpenMoment.tz(
                                timeItem.timezone
                            );

                            unreleasedDates.push(
                                <li key={timeItem.displayTime}>
                                    {`${releaseDateAndTime.format(
                                        releaseDateAndTimeFormat
                                    )}`}
                                    {timeItem.nowMoment.isAfter(releaseDateAndTime) && (
                                        <>
                                            &nbsp; · &nbsp;
                                            <Badge appearance="important" size="lg">
                                                SOLD OUT
                                            </Badge>
                                        </>
                                    )}
                                </li>
                            );

                            unreleasedDatesText?.push(
                                `${releaseDateAndTime?.format(releaseDateAndTimeFormat)}${
                                    timeItem.nowMoment?.isAfter(releaseDateAndTime)
                                        ? ' SOLD OUT'
                                        : ''
                                }`
                            );
                        }
                        if (
                            timeItem.hasSecondary &&
                            timeItem.secondaryOpenMoment &&
                            !timeItem.primaryOpenMoment.isSame(
                                timeItem.secondaryOpenMoment
                            )
                        ) {
                            const releaseDateAndTime = timeItem.secondaryOpenMoment.tz(
                                timeItem.timezone
                            );

                            unreleasedDates.push(
                                <li key={timeItem.displayTime}>
                                    {releaseDateAndTime.format(releaseDateAndTimeFormat)}
                                </li>
                            );

                            unreleasedDatesText?.push(
                                releaseDateAndTime?.format(releaseDateAndTimeFormat)
                            );
                        }

                        alreadyShowedReleaseDate = true;
                    } else if (timeItem?.availableWalkupCount) {
                        walkupTourTimes.push(
                            <li key={timeItem.displayTime}>{timeItem.displayTime}</li>
                        );
                        walkupTourTimesText?.push(timeItem?.displayTime);
                    }
                });
            }

            let contents = null;
            let contentsText = '';
            let showHeading = true;
            let heading = 'Available Times';
            let availabilityText = null;
            if (!isEmpty(tourTimes)) {
                availabilityText = 'Available';
            } else if (isEmpty(tourTimes) && hasFilterDate) {
                const lastResDate = moment(farthestByTour[tourId], 'YYYY-MM-DD');
                availabilityText =
                    showNoTicketRequired(facilityId, facilitiesUsingNoTicketRequired) &&
                    lastResDate?.isSameOrAfter(filterDate) &&
                    !hasScheduledInventory
                        ? 'No Ticket Required'
                        : 'Unavailable';
            }

            if (!hasFilterDate) {
                showHeading = false;
                contentsText =
                    'Enter date to see available times, or click for ticket details.';
                contents = (
                    <Text size="sm" color="gray">
                        {contentsText}
                    </Text>
                );
            } else if (areTimesLoading) {
                contentsText = 'Loading times ...';
                contents = (
                    <Text size="sm" color="gray">
                        {contentsText}
                    </Text>
                );
            } else if (hasTimesLoadingError) {
                contentsText = 'Error loading times. Refresh your browser and try again.';
                contents = (
                    <Text size="sm" color="gray">
                        {contentsText}
                    </Text>
                );
            } else if (availableTourTimes.length) {
                contentsText = `${availableTourTimesText?.join(', ')} - Click here to book now`;
                contents = (
                    <InlineBulletList space="xs">{availableTourTimes}</InlineBulletList>
                );
            } else if (unreleasedDates.length) {
                contentsText = unreleasedDatesText?.join(', ');
                heading = 'Tickets will release on the following date(s):';
                contents = <ul>{unreleasedDates}</ul>;
                availabilityText = 'NOT YET RELEASED';
            } else if (firstComeFirstServedTours?.[tourId] && walkupTourTimes.length) {
                contentsText = walkupTourTimesText?.join(', ');
                contents = (
                    <InlineBulletList space="xs">{walkupTourTimes}</InlineBulletList>
                );
                availabilityText = 'First-come, First-served';
            } else if (hasFilterDate && tourTimes.length === 0) {
                contentsText = 'No available times';
                contents = (
                    <Text size="sm" color="gray">
                        {contentsText}
                    </Text>
                );
            }

            contentAriaLabel += `${showHeading ? ` - ${heading}` : ''}${
                contentsText ? ` - ${contentsText}` : ''
            }`;

            return {
                tourId,
                linkUrl: `/timed-entry/${facilityId}/ticket/${tourId}`,
                imageUrl,
                tourName,
                availabilityText,
                rating,
                reviewCount: reviewCount || 0,
                contentAriaLabel,
                showHeading,
                heading,
                contents,
            };
        });
    };

    const renderToursCardList = () => {
        const toursCardData = getToursCardData();

        const handleMouseOver = (tourId) => () => onCardOver(tourId);

        return toursCardData?.map(
            ({
                tourId,
                linkUrl,
                imageUrl,
                tourName,
                availabilityText,
                rating,
                reviewCount,
                showHeading,
                heading,
                contents,
            }) => (
                <SearchResultCard
                    key={tourId}
                    linkUrl={linkUrl}
                    imageUrl={imageUrl}
                    heading={tourName}
                    availability={availabilityText}
                    rating={rating}
                    reviewCount={reviewCount}
                    onMouseOver={handleMouseOver(tourId)}
                >
                    <Stack space="xs">
                        {showHeading && <Text fontWeight="semibold">{heading}</Text>}
                        {contents}
                    </Stack>
                </SearchResultCard>
            )
        );
    };

    if (!tours || tours.length === 0) {
        return (
            <Alert type="info" heading="No results">
                No tours match your criteria. Try clearing the filters.
            </Alert>
        );
    }

    return (
        <Stack space="md" hasListRole>
            {renderToursCardList()}
        </Stack>
    );
}

TourList.propTypes = {
    tours: PropTypes.arrayOf(PropTypes.object),
    tourImageMap: PropTypes.objectOf(PropTypes.object),
    tourRatingsMap: PropTypes.objectOf(PropTypes.object),
    ticketTimesById: PropTypes.object,
    areTimesLoading: PropTypes.bool,
    hasTimesLoadingError: PropTypes.bool,
    filterDate: PropTypes.object,
    firstComeFirstServedTours: PropTypes.object,
    onCardOverEvent: PropTypes.func,
    farthestByTour: PropTypes.arrayOf(PropTypes.object),
};
